import { Box, Grid, Tabs, Tab, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import usePermissions from '../../hooks/usePermissions';
import {
  fetchCitiesBalance,
  fetchStatistics,
  selectCitiesBalance,
  selectStatistics
} from '../../redux/slices/balanceSlice';
import { selectCities } from '../../redux/slices/citiesSlice';
import { getActiveContractsAsync } from '../../redux/slices/contractsSlice';
import Title from '../../components/Title';
import { DateRange } from '../../types/dateRange';
import { selectAuth } from '../../redux/slices/authSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import TodayStatistic from './components/TodayStatistic';
import WeatherWidget from './components/WeatherWidget';
import UsersStatisticTab from './components/UsersStatisticTab';
import DailyLineChart from './DailyLineChart';
import StyledBarChart from './StyledBarChart';
import SubscriptionStatsChart from '../SubscriptionPage/components/SubscriptionStatsChart';
import WorkerActivityTab from './components/WorkerActivityTab';
import AIDemandAnalyticsTab from './components/AIDemandAnalytics/AIDemandAnalyticsTab';
import FullBatteryTab from './components/FullBatteryTab';
import UserDevicesTab from './components/UserDevicesTab';
import ScootersAnalyticsTab from './components/ScootersAnalyticsTab';
import RentHeatMapSection from '../../components/sections/RentHeatMapSection';
import AiAnalyticsPage from '../AiAnalyticsPage';
import { TripState } from './types';

const defaultCenter = {
  lat: 48.9225224,
  lng: 24.7103188
};

const StatisticPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const checkPermission = usePermissions();
  const dispatch = useDispatch();
  const citiesBalance = useSelector(selectCitiesBalance);
  const wasteCitiesBalance = useSelector(selectStatistics);
  const cities = useSelector(selectCities);
  const auth = useSelector(selectAuth);
  const adminInfo = useSelector(selectAdmins);

  const [currentTab, setCurrentTab] = useState(0);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const isSuperAdmin = useMemo(() => {
    return auth.info?.type === 'superAdmin';
  }, [auth.info?.type]);

  const currentCityId = useMemo(() => {
    // Use the city ID directly from admin info, no need for validation lookup
    return adminInfo.currentCity;
  }, [adminInfo.currentCity]);

  // Separate periods for each chart
  const [tripsPeriod, setTripsPeriod] = useState<DateRange<Moment | null>>([
    moment().startOf('day'),
    moment().endOf('day')
  ]);
  
  const [incomePeriod, setIncomePeriod] = useState<DateRange<Moment | null>>([
    moment().startOf('day'),
    moment().add(1, 'day').endOf('day')
  ]);
  
  const [expensePeriod, setExpensePeriod] = useState<DateRange<Moment | null>>([
    moment().startOf('day'),
    moment().add(1, 'day').endOf('day')
  ]);

  const [tripsData, setTripsData] = useState<TripState[]>([]);

  // Fetch trips data
  useEffect(() => {
    if (checkPermission('contracts') && (isSuperAdmin || currentCityId)) {
      const fetchData = async () => {
        if (isSuperAdmin && cities.items.length > 0) {
          // For superadmin, fetch data for all cities
          const promises = cities.items.map(async (city) => {
            const result: any = await dispatch(
              getActiveContractsAsync({
                active: false,
                from: moment(tripsPeriod[0]).unix() * 1000,
                to: moment(tripsPeriod[1]).endOf('day').unix() * 1000,
                cityId: city._id
              })
            );
            return {
              cityName: city.cityName,
              count: result?.contractsCount ?? 0
            };
          });
          const results = await Promise.all(promises);
          // Sort by city name for consistency
          setTripsData(results.sort((a, b) => a.cityName.localeCompare(b.cityName)));
        } else if (!isSuperAdmin && currentCityId) {
          // For regular admin, fetch only for current city
          try {
            const city = cities.items.find(c => c._id === currentCityId);
            
            const result: any = await dispatch(
              getActiveContractsAsync({
                active: false,
                from: moment(tripsPeriod[0]).unix() * 1000,
                to: moment(tripsPeriod[1]).endOf('day').unix() * 1000,
                cityId: currentCityId
              })
            );
            
            setTripsData([{
              cityName: city?.cityName || 'Unknown',
              count: result?.contractsCount ?? 0
            }]);
          } catch (error) {
            console.error('Error fetching trips data:', error);
          }
        }
      };

      fetchData();
    }
  }, [tripsPeriod, checkPermission, dispatch, currentCityId, isSuperAdmin, cities.items]);

  // Fetch income data
  useEffect(() => {
    if ((isSuperAdmin || checkPermission('statistics')) && (isSuperAdmin || currentCityId)) {
      dispatch(
        fetchCitiesBalance({
          from: incomePeriod[0] ? moment(incomePeriod[0]).subtract(1, 'millisecond').toISOString() : undefined,
          to: incomePeriod[1] ? moment(incomePeriod[1]).add(1, 'day').startOf('day').toISOString() : undefined,
          cityId: isSuperAdmin ? undefined : currentCityId
        })
      );
    }
  }, [isSuperAdmin, incomePeriod, dispatch, checkPermission, currentCityId]);

  // Fetch expense data
  useEffect(() => {
    if ((isSuperAdmin || checkPermission('statistics')) && (isSuperAdmin || currentCityId)) {
      dispatch(
        fetchStatistics({
          dateFrom: expensePeriod[0]?.toISOString(),
          dateTo: expensePeriod[1] ? moment(expensePeriod[1]).add(1, 'day').startOf('day').toISOString() : undefined,
          cityId: isSuperAdmin ? undefined : currentCityId
        })
      );
    }
  }, [expensePeriod, dispatch, isSuperAdmin, checkPermission, currentCityId]);

  // Update map center when city changes
  useEffect(() => {
    let nextMapCenter;

    if (auth.info?.type === 'superAdmin') {
      const city = cities.items.find(item => item._id === adminInfo.currentCity);
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    } else if (adminInfo.info && adminInfo.currentCity) {
      const city = adminInfo.info.cities.find(
        item => item._id === adminInfo.currentCity
      );
      if (city?.lat && city.lon) {
        nextMapCenter = {
          lat: city.lat,
          lng: city.lon
        };
      }
    }

    if (!nextMapCenter) {
      setMapCenter(defaultCenter);
    } else {
      setMapCenter(nextMapCenter);
    }
  }, [adminInfo.currentCity, adminInfo.info, auth.info, cities.items]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box>
      <Title sx={{ mb: 3 }}>{t('stats.title')}</Title>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
              '&.Mui-selected': {
                color: theme.palette.mode === 'light' ? '#424242' : '#5073F8',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.mode === 'light' ? '#424242' : '#5073F8',
              height: 3,
            },
          }}
        >
          <Tab label={t('stats.tabMain')} />
          <Tab label={t('stats.tabSubscriptions')} />
          <Tab label={t('stats.tabFinancial')} />
          <Tab label={t('stats.tabWorkerActivity')} />
          <Tab label={t('stats.tabDemandAnalytics')} />
          <Tab label={t('stats.tabScooters')} />
          <Tab label={t('stats.tabUsers')} />
          {isSuperAdmin && <Tab label={t('stats.tabAi')} />}
          {isSuperAdmin && <Tab label={t('stats.tabFullBattery')} />}
          {isSuperAdmin && <Tab label={t('stats.tabUserDevices')} />}
        </Tabs>
      </Box>

      {currentTab === 0 && (
        <Grid container spacing={3}>
          {(isSuperAdmin || checkPermission('statistics') || checkPermission('contracts')) && (
            <>
              <Grid item xs={12} md={8}>
                <TodayStatistic />
              </Grid>
              <Grid item xs={12} md={4}>
                <WeatherWidget />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <DailyLineChart />
          </Grid>
        </Grid>
      )}

      {currentTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SubscriptionStatsChart />
          </Grid>
        </Grid>
      )}

      {currentTab === 2 && (
        <Grid container spacing={3}>
          {checkPermission('contracts') && (
            <Grid item xs={12} md={6}>
              <StyledBarChart
                data={tripsData}
                label={t('stats.tripsCount')}
                barDataKey='count'
                xDataKey='cityName'
                value={tripsPeriod}
                setValue={setTripsPeriod}
              />
            </Grid>
          )}

          {(isSuperAdmin || checkPermission('statistics')) && (
            <>
              <Grid item xs={12} md={6}>
                <StyledBarChart
                  data={citiesBalance}
                  label={t('stats.incomeAmount')}
                  barDataKey='count'
                  xDataKey='cityName'
                  value={incomePeriod}
                  setValue={setIncomePeriod}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledBarChart
                  data={wasteCitiesBalance}
                  label={t('stats.expenseAmount')}
                  barDataKey='count'
                  xDataKey='city'
                  isWaste={true}
                  value={expensePeriod}
                  setValue={setExpensePeriod}
                />
              </Grid>
            </>
          )}
        </Grid>
      )}

      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <WorkerActivityTab />
          </Grid>
        </Grid>
      )}

      {currentTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <AIDemandAnalyticsTab mapCenter={mapCenter} currentCityId={currentCityId || ''} />
          </Grid>
        </Grid>
      )}

      {currentTab === 5 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ScootersAnalyticsTab />
          </Grid>
        </Grid>
      )}

      {currentTab === 6 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <UsersStatisticTab />
          </Grid>
        </Grid>
      )}

      {currentTab === 7 && isSuperAdmin && (
        <Box sx={{ mt: 2 }}>
          <AiAnalyticsPage />
        </Box>
      )}

      {currentTab === 8 && isSuperAdmin && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FullBatteryTab />
          </Grid>
        </Grid>
      )}

      {currentTab === 9 && isSuperAdmin && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <UserDevicesTab />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default StatisticPage;
